import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CountUp from "react-countup";
import { formatCurrency } from "../lib/formatCurrency";
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Pencil, Trash2} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getLeads, createLead, updateLead, deleteLead, importLeadsCSV, getOpportunities, createOpportunity, updateOpportunity, deleteOpportunity, getQuotations, createQuotation, updateQuotation, deleteQuotation, convertQuotationToInvoice, getQuotationPDF, getClients, emailQuotation } from '../lib/api';
import { Plus, Search, Filter, MoreVertical, Phone, Mail, Building, TrendingUp, FileText, ArrowRight, Upload, FileUp, Download, Eye, Lock, Check, ChevronsUpDown, UserPlus, LayoutGrid, List } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { DisplayOptionsBar, DEFAULT_DISPLAY_OPTIONS, loadRememberedDocSettings, rememberDocSettings, recalcItems, lineTotal, symbolFor } from '../components/docDisplayOptions';
import { CRMRecordPanel } from '../components/CRMRecordPanel';
import { RecordAttachments } from '../components/RecordAttachments';

const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'unqualified'];
const LEAD_SOURCES = ['manual', 'website', 'referral', 'social', 'event', 'cold_call'];
const OPP_STAGES = ['qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  qualified: 'bg-emerald-100 text-emerald-700',
  unqualified: 'bg-slate-100 text-slate-700',
  qualification: 'bg-blue-100 text-blue-700',
  proposal: 'bg-amber-100 text-amber-700',
  negotiation: 'bg-purple-100 text-purple-700',
  closed_won: 'bg-emerald-100 text-emerald-700',
  closed_lost: 'bg-rose-100 text-rose-700',
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  converted: 'bg-amber-100 text-amber-700'
};

// Lead Form Component
const LeadForm = ({ lead, onSave, onClose }) => {
  const [form, setForm] = useState(lead || {
    name: '', email: '', phone: '', company: '', source: 'manual', score: 0, status: 'new', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (lead?.id) {
        await updateLead(lead.id, form);
        toast.success('Lead updated');
      } else {
        await createLead(form);
        toast.success('Lead created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Company</Label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Score (0-100)</Label>
          <Input type="number" min="0" max="100" value={form.score} onChange={(e) => setForm({ ...form, score: parseInt(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
      </div>
      {lead?.id && (
        <div className="border-t pt-3">
          <RecordAttachments parentType="lead" parentId={lead.id} />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-lead-btn">{saving ? 'Saving...' : 'Save Lead'}</Button>
      </div>
    </form>
  );
};

// Opportunity Form Component
const OpportunityForm = ({ opportunity, onSave, onClose }) => {
  const [form, setForm] = useState(opportunity || {
    name: '', stage: 'qualification', value: 0, probability: 10, expected_close_date: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (opportunity?.id) {
        await updateOpportunity(opportunity.id, form);
        toast.success('Opportunity updated');
      } else {
        await createOpportunity(form);
        toast.success('Opportunity created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save opportunity');
    } finally {
      setSaving(false);
    }
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Opportunity Name *</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="opp-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Stage</Label>
          <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {OPP_STAGES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Probability (%)</Label>
          <Input type="number" min="0" max="100" value={form.probability} onChange={(e) => setForm({ ...form, probability: parseInt(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Value (ZAR)</Label>
          <Input type="number" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="space-y-2">
          <Label>Expected Close Date</Label>
          <Input type="date" value={form.expected_close_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-opp-btn">{saving ? 'Saving...' : 'Save Opportunity'}</Button>
      </div>
    </form>
  );
};

// Quotation Form Component (invoice-parity)
const QuotationForm = ({ quotation, clients = [], onSave, onClose }) => {
  const DEFAULT_TERMS = `• All prices are in Rands.
• The contract price is stated in the quotation and it may be varied in accordance with conditions.
• Late payment entitles the supplier to interest at statutory rate of 10%.
• Cancellation of quotation after being accepted may result in 25% penalty.
• Payment: Payment can be made by electronic transfer, and must be received prior to training dates. Please quote the reference number from your invoice so that payment can be tracked. Your reservation will be cancelled should payment not be received prior to the training date unless a purchase order is provided.
• Cancellation and Substitutions: All cancellations must be done in writing and emailed directly to IKWORX. Less than five (5) working days = 100% of the course fee. Substitutions can be made at any time without incurring a penalty.
• Transfers & Postponements: Transfers can only be made up to five (5) working days prior to the course date. The option to transfer can only be used once within (3) three months.`;

  const DEFAULT_BANK = {
    bank: 'NedBank',
    account_name: 'Innovative Knowledge Worx Trading and Projects (PTY)LTD',
    account_number: '1274012465',
    branch: 'RRB MELROSE ARCH',
    branch_code: '198765',
  };

  const remembered = loadRememberedDocSettings();
  const [form, setForm] = useState(
    quotation
      ? {
          ...quotation,
          items: (quotation.items && quotation.items.length > 0
            ? quotation.items
            : [{ description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }]
          ).map((it) => ({
            description: it.description || '',
            quantity: it.quantity ?? 1,
            incl_price: it.incl_price ?? it.unit_price ?? 0,
            disc_pct: it.disc_pct ?? 0,
            disc_amount: it.disc_amount ?? 0,
            vat_pct: it.vat_pct ?? it.vat_rate ?? 15,
          })),
          bank_details: quotation.bank_details || DEFAULT_BANK,
          terms_and_conditions: quotation.terms_and_conditions || DEFAULT_TERMS,
          overall_discount_pct: quotation.overall_discount_pct ?? 0,
          reference: quotation.reference || '',
          sales_rep: quotation.sales_rep || '',
          client_vat_no: quotation.client_vat_no || '',
          client_postal_address: quotation.client_postal_address || '',
          client_physical_address: quotation.client_physical_address || '',
          valid_until: quotation.valid_until?.split('T')[0] || '',
          display_options: quotation.display_options || { ...DEFAULT_DISPLAY_OPTIONS },
          currency: quotation.currency || 'ZAR',
        }
      : {
          client_name: '',
          client_email: '',
          client_vat_no: '',
          client_postal_address: '',
          client_physical_address: '',
          reference: '',
          sales_rep: '',
          overall_discount_pct: 0,
          items: [{ description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }],
          subtotal: 0,
          vat_rate: 15,
          vat_amount: 0,
          total: 0,
          currency: remembered?.currency || 'ZAR',
          display_options: remembered?.display_options || { ...DEFAULT_DISPLAY_OPTIONS },
          status: 'draft',
          valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          notes: '',
          bank_details: DEFAULT_BANK,
          terms_and_conditions: DEFAULT_TERMS,
        }
  );
  const [saving, setSaving] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [useManualClient, setUseManualClient] = useState(false);

  const applyClient = (c) => {
    if (!c) return;
    setForm({
      ...form,
      client_id: c.id,
      client_name: c.company || '',
      client_email: c.contact_email || '',
      client_vat_no: c.vat_number || '',
      client_postal_address: c.billing_address || form.client_postal_address || '',
      client_physical_address: c.billing_address || form.client_physical_address || '',
    });
    setClientPickerOpen(false);
    setUseManualClient(false);
    toast.success(`Loaded ${c.company}'s details`);
  };

  // Calculate totals from line items, respecting display options
  const recalc = (items, dopts = form.display_options) => recalcItems(items, dopts || DEFAULT_DISPLAY_OPTIONS);

  const setDisplayOptions = (dopts) => setForm({ ...form, display_options: dopts, ...recalcItems(form.items, dopts) });
  const setCurrency = (cur) => setForm({ ...form, currency: cur });
  const opts = form.display_options || DEFAULT_DISPLAY_OPTIONS;
  const curSym = symbolFor(form.currency || 'ZAR');
  const fmtMoney = (v) => `${curSym}${(Number(v) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems, ...recalc(newItems) });
  };

  const addItem = () => {
    const newItems = [...form.items, { description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }];
    setForm({ ...form, items: newItems, ...recalc(newItems) });
  };

  const removeItem = (idx) => {
    const remaining = form.items.filter((_, i) => i !== idx);
    const newItems = remaining.length ? remaining : [{ description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }];
    setForm({ ...form, items: newItems, ...recalc(newItems) });
  };

  const setBank = (field, value) => setForm({ ...form, bank_details: { ...(form.bank_details || {}), [field]: value } });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name) {
      toast.error('Please select or enter a client name');
      return;
    }
    setSaving(true);
    try {
      if (quotation?.id) {
        await updateQuotation(quotation.id, form);
        toast.success('Quotation updated');
      } else {
        await createQuotation(form);
        toast.success('Quotation created');
      }
      rememberDocSettings(form.display_options, form.currency);
      onSave();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[78vh] overflow-y-auto pr-2">
      {/* Top meta */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label>Sales Rep</Label>
          <Input value={form.sales_rep || ''} onChange={(e) => setForm({ ...form, sales_rep: e.target.value })} placeholder="e.g. Fulufhelo Matodzi" data-testid="quote-sales-rep" />
        </div>
        <div className="space-y-1">
          <Label>Valid Until</Label>
          <Input type="date" value={form.valid_until || ''} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid-until" />
        </div>
        <div className="space-y-1">
          <Label>Overall Discount %</Label>
          <Input type="number" min="0" max="100" step="0.01" value={form.overall_discount_pct ?? 0} onChange={(e) => setForm({ ...form, overall_discount_pct: parseFloat(e.target.value) || 0 })} data-testid="quote-overall-discount" />
        </div>
      </div>
      <div className="space-y-1">
        <Label>Reference</Label>
        <Input value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Course / Unit Standard / PO number" data-testid="quote-reference" />
      </div>

      {/* Display options + currency */}
      <DisplayOptionsBar options={opts} onChange={setDisplayOptions} currency={form.currency || 'ZAR'} onCurrencyChange={setCurrency} idPrefix="quote" />

      {/* Client (TO) block */}
      <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
        <p className="text-sm font-semibold">Prepared For (Customer)</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Client Name *</Label>
            {useManualClient ? (
              <div className="flex gap-2">
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required autoFocus data-testid="quote-client-input" />
                <Button type="button" variant="outline" onClick={() => setUseManualClient(false)} title="Pick from client list" data-testid="quote-client-use-picker">
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="quote-client-picker">
                    <span className={form.client_name ? '' : 'text-slate-400'}>{form.client_name || 'Select a client...'}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients..." data-testid="quote-client-search" />
                    <CommandList>
                      <CommandEmpty>No clients found.</CommandEmpty>
                      <CommandGroup heading="Clients">
                        {clients.map((c) => (
                          <CommandItem key={c.id} value={c.company || c.id} onSelect={() => applyClient(c)} data-testid={`quote-client-option-${c.id}`}>
                            <Check className={`mr-2 h-4 w-4 ${form.client_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="min-w-0">
                              <p className="text-sm truncate">{c.company}</p>
                              {c.contact_email && <p className="text-xs text-slate-500 truncate">{c.contact_email}</p>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setUseManualClient(true); setClientPickerOpen(false); }} data-testid="quote-client-manual">
                          <UserPlus className="mr-2 h-4 w-4" /> Enter client manually
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <div className="space-y-1">
            <Label>Customer VAT No</Label>
            <Input value={form.client_vat_no || ''} onChange={(e) => setForm({ ...form, client_vat_no: e.target.value })} data-testid="quote-client-vat" />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Client Email</Label>
          <Input type="email" value={form.client_email || ''} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Postal Address</Label>
            <Textarea rows={3} value={form.client_postal_address || ''} onChange={(e) => setForm({ ...form, client_postal_address: e.target.value })} placeholder="Street&#10;Suburb&#10;City&#10;Postal Code" data-testid="quote-client-postal" />
          </div>
          <div className="space-y-1">
            <Label>Physical Address</Label>
            <Textarea rows={3} value={form.client_physical_address || ''} onChange={(e) => setForm({ ...form, client_physical_address: e.target.value })} placeholder="Street&#10;Suburb&#10;City&#10;Postal Code" data-testid="quote-client-physical" />
          </div>
        </div>
      </div>

      {/* Line items table */}
      <div className="space-y-2">
        <Label>Line Items</Label>
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-1 bg-slate-100 px-2 py-1.5 text-xs font-semibold">
            <div className="col-span-4">Description</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">{opts.include_tax_in_price !== false ? 'Incl. Price' : 'Price (Excl.)'}</div>
            {!opts.hide_discount && <div className="col-span-1 text-right">{opts.percentage_discounts !== false ? 'Disc %' : `Disc (${curSym.trim()})`}</div>}
            {!opts.hide_tax && <div className="col-span-1 text-right">VAT %</div>}
            <div className="col-span-2 text-right">Total</div>
          </div>
          <div className="divide-y">
            {form.items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center">
                <div className="col-span-4">
                  <Input className="h-8" placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} data-testid={`quote-item-desc-${idx}`} />
                </div>
                <Input className="col-span-1 h-8 text-right" type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} data-testid={`quote-item-qty-${idx}`} />
                <Input className="col-span-2 h-8 text-right" type="number" min="0" step="0.01" value={item.incl_price} onChange={(e) => updateItem(idx, 'incl_price', parseFloat(e.target.value) || 0)} data-testid={`quote-item-incl-price-${idx}`} />
                {!opts.hide_discount && (opts.percentage_discounts !== false ? (
                  <Input className="col-span-1 h-8 text-right" type="number" min="0" max="100" step="0.01" value={item.disc_pct} onChange={(e) => updateItem(idx, 'disc_pct', parseFloat(e.target.value) || 0)} />
                ) : (
                  <Input className="col-span-1 h-8 text-right" type="number" min="0" step="0.01" value={item.disc_amount} onChange={(e) => updateItem(idx, 'disc_amount', parseFloat(e.target.value) || 0)} />
                ))}
                {!opts.hide_tax && (
                  <Input className="col-span-1 h-8 text-right" type="number" min="0" max="100" step="0.01" value={item.vat_pct} onChange={(e) => updateItem(idx, 'vat_pct', parseFloat(e.target.value) || 0)} />
                )}
                <div className="col-span-2 text-right text-sm font-mono">{fmtMoney(lineTotal(item, opts))}</div>
                <button type="button" className="col-span-1 text-red-500 hover:text-red-700 text-xs" onClick={() => removeItem(idx)} title="Remove line">✕ Remove</button>
              </div>
            ))}
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>+ Add Line</Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-4 text-right pt-2 border-t">
        {!opts.hide_exclusive_total && (<>
          <div></div>
          <div className="text-sm text-muted-foreground">Total Exclusive:</div>
          <div className="font-mono" data-testid="quote-subtotal">{fmtMoney(form.subtotal)}</div>
        </>)}
        {!opts.hide_tax && (<>
          <div></div>
          <div className="text-sm text-muted-foreground">Total VAT:</div>
          <div className="font-mono" data-testid="quote-vat-total">{fmtMoney(form.vat_amount)}</div>
        </>)}
        <div></div>
        <div className="font-semibold">Grand Total:</div>
        <div className="font-mono font-bold text-lg" data-testid="quote-grand-total">{fmtMoney(form.total)}</div>
      </div>

      {/* Bank details */}
      <details className="border rounded-lg p-3" open>
        <summary className="text-sm font-semibold cursor-pointer">Bank Details (shown on PDF)</summary>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="space-y-1"><Label>Bank</Label><Input value={form.bank_details?.bank || ''} onChange={(e) => setBank('bank', e.target.value)} /></div>
          <div className="space-y-1"><Label>Account Name</Label><Input value={form.bank_details?.account_name || ''} onChange={(e) => setBank('account_name', e.target.value)} /></div>
          <div className="space-y-1"><Label>Account Number</Label><Input value={form.bank_details?.account_number || ''} onChange={(e) => setBank('account_number', e.target.value)} /></div>
          <div className="space-y-1"><Label>Branch</Label><Input value={form.bank_details?.branch || ''} onChange={(e) => setBank('branch', e.target.value)} /></div>
          <div className="space-y-1"><Label>Branch Code</Label><Input value={form.bank_details?.branch_code || ''} onChange={(e) => setBank('branch_code', e.target.value)} /></div>
        </div>
      </details>

      {/* Terms */}
      <details className="border rounded-lg p-3">
        <summary className="text-sm font-semibold cursor-pointer">Terms &amp; Conditions (shown on PDF)</summary>
        <Textarea className="mt-3" rows={6} value={form.terms_and_conditions || ''} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })} data-testid="quote-terms" />
      </details>

      {/* Notes */}
      <div className="space-y-1">
        <Label>Notes (optional)</Label>
        <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t bg-background sticky bottom-0">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-quote-btn">
          {saving ? 'Saving...' : quotation ? 'Update Quotation' : 'Create Quotation'}
        </Button>
      </div>
    </form>
  );
};

// Main Sales Page Component
export const SalesPage = () => {
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [oppDialogOpen, setOppDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  
  // Get initial tab from URL query parameter
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "leads");

  useEffect(() => {
    loadData();
  }, []);

  // Update tab when URL changes
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const loadData = async () => {
    try {
      const [leadsRes, oppsRes, quotesRes, clientsRes] = await Promise.all([
        getLeads(), getOpportunities(), getQuotations(), getClients()
      ]);
      setLeads(leadsRes.data);
      setOpportunities(oppsRes.data);
      setQuotations(quotesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (id) => {
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleDeleteOpportunity = async (id) => {
  if (!window.confirm("Delete this opportunity?")) return;

  try {
    await deleteOpportunity(id);
    toast.success("Opportunity deleted");
    loadData();
  } catch (error) {
    console.error(error);
    toast.error("Failed to delete opportunity");
  }
};

  const handleConvertToInvoice = async (quoteId) => {
  try {
    await convertQuotationToInvoice(quoteId);
    toast.success('Quotation converted to invoice');
    loadData();
  } catch (error) {
    toast.error('Failed to convert quotation');
  }
};

const handleEditQuote = (quote) => {
  setSelectedQuote(quote);
  setQuoteDialogOpen(true);
};

const handleDeleteQuote = async (id) => {
  if (!window.confirm("Delete this quotation?")) return;

  try {
    await deleteQuotation(id);
    toast.success("Quotation deleted");
    loadData();
  } catch (error) {
    console.error(error);
    toast.error("Failed to delete quotation");
  }
};

const handleCSVImport = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (!file.name.endsWith('.csv')) {
    toast.error('Please select a CSV file');
    return;
  }

  setImporting(true);

  try {
    const text = await file.text();
    const response = await importLeadsCSV(text);
    const { imported_count, failed_count, errors } = response.data;

    if (imported_count > 0) {
      toast.success(`Successfully imported ${imported_count} leads`);
    }

    if (failed_count > 0) {
      toast.warning(`${failed_count} rows failed to import`);
      if (errors?.length > 0) {
        console.log('Import errors:', errors);
      }
    }

    loadData();
    setImportDialogOpen(false);

  } catch (error) {
    console.error('Import error:', error);
    toast.error('Failed to import CSV file');
  } finally {
    setImporting(false);
    e.target.value = '';
  }
};

  const [leadView, setLeadView] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 1024 ? 'cards' : 'table'));

  const filteredLeads = leads.filter(l => 
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.company?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredOpps = opportunities.filter(o => 
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sales & CRM</h1>
          <p className="text-muted-foreground mt-1">Manage leads, opportunities, and quotations</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search..." 
              className="pl-9 w-full sm:w-64" 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="sales-search"
            />
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="leads" data-testid="leads-tab">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="opportunities-tab">Opportunities ({opportunities.length})</TabsTrigger>
          <TabsTrigger value="quotations" data-testid="quotations-tab">Quotations ({quotations.length})</TabsTrigger>
        </TabsList>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center border rounded-md overflow-hidden mr-auto" data-testid="leads-view-toggle">
              <button
                type="button"
                onClick={() => setLeadView('table')}
                className={`p-2.5 ${leadView === 'table' ? 'bg-black text-[#D4E100]' : 'bg-white text-slate-500'}`}
                data-testid="leads-view-table-btn"
                aria-label="Table view"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setLeadView('cards')}
                className={`p-2.5 ${leadView === 'cards' ? 'bg-black text-[#D4E100]' : 'bg-white text-slate-500'}`}
                data-testid="leads-view-cards-btn"
                aria-label="Card view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
            {/* CSV Import Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-leads-btn">
                  <Upload className="h-4 w-4 mr-2" /> Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Import Leads from CSV</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV file containing leads data. The file should include columns like: Name, Email, Phone, Company, Status, Source, etc.
                  </p>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileUp className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <label className="cursor-pointer">
                      <span className="text-primary font-medium hover:underline">
                        {importing ? 'Importing...' : 'Choose CSV file'}
                      </span>
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleCSVImport}
                        disabled={importing}
                        data-testid="csv-file-input"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported columns: Name, Email, Phone, Company, Status, Source, Description, Country, City, Tags
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Lead Dialog */}
            <Dialog open={leadDialogOpen} onOpenChange={setLeadDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedLead(null)} data-testid="add-lead-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedLead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
                </DialogHeader>
                <LeadForm lead={selectedLead} onSave={loadData} onClose={() => setLeadDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {/* Leads Card View */}
          {leadView === 'cards' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="leads-card-view">
              {filteredLeads.map((lead) => (
                <Card key={lead.id} data-testid={`lead-card-${lead.id}`} className={lead._is_locked ? 'bg-red-50/30' : ''}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium flex items-center gap-1.5">
                          {lead._is_locked && <Lock className="h-3 w-3 text-red-500 shrink-0" />}
                          <span className="truncate">{lead.name}</span>
                        </p>
                        {lead.company && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Building className="h-3 w-3 shrink-0" />
                            <span className="truncate">{lead.company}</span>
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="-mr-2" data-testid={`lead-card-menu-${lead.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedLead(lead); setLeadDialogOpen(true); }}>Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-rose-600" onClick={() => handleDeleteLead(lead.id)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="space-y-1 text-sm">
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="text-primary hover:underline flex items-center gap-1.5">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate">{lead.email}</span>
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="text-muted-foreground hover:text-primary flex items-center gap-1.5">
                          <Phone className="h-3 w-3 shrink-0" />
                          <span>{lead.phone}</span>
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={statusColors[lead.status]}>{lead.status}</Badge>
                      <Badge variant="outline" className="font-mono">Score {lead.score}</Badge>
                      <span className="text-xs text-muted-foreground capitalize">{lead.source.replace('_', ' ')}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredLeads.length === 0 && (
                <div className="col-span-full text-center py-10 text-muted-foreground border rounded-lg bg-white">
                  No leads found
                </div>
              )}
            </div>
          )}

          {leadView === 'table' && (
          <Card>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className={lead._is_locked ? 'bg-red-50/30' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {lead._is_locked && <Lock className="h-3 w-3 text-red-500" />}
                        {lead.name}
                      </div>
                    </TableCell>
                    <TableCell>{lead.company || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {lead.email ? (
                        <a href={`mailto:${lead.email}`} className="text-primary hover:underline flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[180px]">{lead.email}</span>
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {lead.phone ? (
                        <a href={`tel:${lead.phone}`} className="text-muted-foreground hover:text-primary flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span>{lead.phone}</span>
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="capitalize text-sm">{lead.source.replace('_', ' ')}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono">{lead.score}</Badge></TableCell>
                    <TableCell><Badge className={statusColors[lead.status]}>{lead.status}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedLead(lead); setLeadDialogOpen(true); }}>Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-rose-600" onClick={() => handleDeleteLead(lead.id)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLeads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No leads found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </div>
          </Card>
          )}
          
        </TabsContent>

        {/* Opportunities Tab - Kanban Style */}
        <TabsContent value="opportunities" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={oppDialogOpen} onOpenChange={setOppDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedOpp(null)} data-testid="add-opp-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Opportunity
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedOpp ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
                </DialogHeader>
                <OpportunityForm opportunity={selectedOpp} onSave={loadData} onClose={() => setOppDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {OPP_STAGES.map((stage) => (
              <div key={stage} className="kanban-column">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold capitalize text-sm">{stage.replace('_', ' ')}</h3>
                  <Badge variant="outline" className="font-mono">
  <CountUp
    end={opportunities
      .filter(o => o.stage === stage)
      .reduce((s, o) => s + o.value, 0)}
    duration={1.2}
    formattingFn={formatCurrency}
  />
</Badge>
                </div>
                <div className="space-y-3">
                  {opportunities.filter(o => o.stage === stage).map((opp) => (
                    <Card key={opp.id} className={`cursor-pointer hover:shadow-md transition-shadow ${opp._is_masked ? 'opacity-70 border-dashed' : ''} ${opp._is_locked ? 'border-l-4 border-l-red-400' : ''}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-1.5 flex-1">
                            {opp._is_locked && <Lock className="h-3 w-3 text-red-500 shrink-0" />}
                            <p className="font-medium text-sm cursor-pointer" onClick={() => { setSelectedOpp(opp); setOppDialogOpen(true); }}>{opp.name}</p>
                          </div>
                          <DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
      <MoreVertical className="h-3 w-3" />
    </Button>
  </DropdownMenuTrigger>

  <DropdownMenuContent align="end">

    <DropdownMenuItem
      onClick={() => {
        setSelectedOpp(opp);
        setOppDialogOpen(true);
      }}
    >
      Edit
    </DropdownMenuItem>

    <DropdownMenuItem
      className="text-red-600"
      onClick={() => handleDeleteOpportunity(opp.id)}
    >
      Delete
    </DropdownMenuItem>

  </DropdownMenuContent>
</DropdownMenu>
                        </div>
                        <div className="flex items-center justify-between" onClick={() => { setSelectedOpp(opp); setOppDialogOpen(true); }}>
                          <span className="font-mono text-sm font-semibold text-amber-600">{opp._is_masked ? '***' : formatCurrency(opp.value)}</span>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-xs">{opp._is_masked ? '***' : `${opp.probability}%`}</Badge>
                            {opp._is_masked && <Badge variant="outline" className="text-[10px] text-red-500 border-red-200">Restricted</Badge>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </TabsContent>

        {/* Quotations Tab */}
        <TabsContent value="quotations" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={quoteDialogOpen} onOpenChange={(open) => { setQuoteDialogOpen(open); if (!open) setSelectedQuote(null); }}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedQuote(null)} data-testid="add-quote-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Quotation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                   <DialogTitle>
    {selectedQuote ? "Edit Quotation" : "Create Quotation"}
  </DialogTitle>
                </DialogHeader>
                <QuotationForm
  key={selectedQuote?.id || 'new'}
  quotation={selectedQuote}
  clients={clients}
  onSave={loadData}
  onClose={() => setQuoteDialogOpen(false)}
/>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                    <TableCell className="font-medium">{quote.client_name}</TableCell>
                    <TableCell>{quote.items?.length || 0} items</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatCurrency(quote.total)}</TableCell>
                    <TableCell><Badge className={statusColors[quote.status]}>{quote.status}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">

  <DropdownMenuItem onClick={() => handleEditQuote(quote)}>
    <Pencil className="h-4 w-4 mr-2" />
    Edit
  </DropdownMenuItem>

  <DropdownMenuItem
    className="text-red-600"
    onClick={() => handleDeleteQuote(quote.id)}
  >
    <Trash2 className="h-4 w-4 mr-2" />
    Delete
  </DropdownMenuItem>

  <DropdownMenuItem
    onClick={async () => {
      if (!quote.client_email) {
        toast.error('No client email on this quotation — edit it and add one first');
        return;
      }
      if (!window.confirm(`Email quotation ${quote.quote_number} to ${quote.client_email}? It will be sent from your email address.`)) return;
      try {
        const res = await emailQuotation(quote.id);
        toast.success(res.data.message);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to email quotation');
      }
    }}
    data-testid={`email-quote-${quote.id}`}
  >
    <Mail className="h-4 w-4 mr-2" />
    Email to Client
  </DropdownMenuItem>

  <DropdownMenuItem
    onClick={async () => {
      try {
        const response = await getQuotationPDF(quote.id);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", `quote_${quote.quote_number}.pdf`);

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

        toast.success("Quotation PDF downloaded");
      } catch (error) {
        toast.error("Failed to download PDF");
      }
    }}
  >
    <Download className="h-4 w-4 mr-2" />
    Download PDF
  </DropdownMenuItem>

  {quote.status !== "converted" && (
    <DropdownMenuItem onClick={() => handleConvertToInvoice(quote.id)}>
      <ArrowRight className="h-4 w-4 mr-2" />
      Convert to Invoice
    </DropdownMenuItem>
  )}

</DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {quotations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No quotations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
